import * as Sentry from '@sentry/vue'
import dayjs from 'dayjs'
import {
  trySetLocalStorage,
  tryGetLocalStorage,
  tryRemoveLocalStorage,
} from '@packages/utils/storage.ts'

import { getGravatarByEmail, getPictureUrl } from '@layers/web/utils/auth/auth.ts'
import { createAuthFeedback } from '@layers/web/utils/auth/feedback.ts'
import { Contexts } from '@layers/web/constants/contexts.ts'
import localStorageKeys from '@layers/web/constants/local-storage-keys.ts'
import { useLocaleStore } from '@layers/web/stores/locale.js'
import type {
  UserStoreState,
  User,
  Booking,
  Favourite,
  AuthFeedback,
  MemberProfile,
  MemberSingleItem,
} from '@layers/web/stores/types/user.ts'
import type { LoginAction, LoginActionWithFeedback } from '@layers/web/utils/auth/types/auth.ts'

const useUserStore = defineStore('user', {
  state: (): UserStoreState => ({
    user: null,
    token: null,
    loading: false,
    bookings: [],
    favourites: [],
    authFeedback: null,
    feedback: null,
    resendCodeEmail: null,
    loginRequest: null,
    isInitiated: false,
    memberPagePath: '',
    memberPageTab: 'logga-in',
    memberPageTabs: [],
    memberActiveSingle: false,
    memberProfile: null,
    memberSingleList: [],
  }),
  persist: {
    paths: [
      'favourites',
      'bookings',
      'user',
      'token',
      'memberActiveSingle',
    ],
    storage: localPersistedState.localStorage,
  },
  getters: {
    getToken: (state): string => {
      if (import.meta.client) {
        const token = tryGetLocalStorage(localStorageKeys.auth.token)
        return state.token || token || ''
      }

      return ''
    },
    getTokenForRequest (): string {
      return this.getToken.replace('Bearer%20', '')
    },
    getFavourites: (state): Favourite[] => {
      return state.favourites || []
    },
    getIsInitiated: (state): boolean => {
      return state.isInitiated
    },
    noFavourites (): boolean {
      return this.getFavourites.length === 0
    },
    populatedFavourites (): Favourite[] {
      return this.getFavourites.filter(x => x?.id && x?.url)
    },
    unpopulatedFavourites (): Favourite[] {
      return this.getFavourites.filter(x => x?.id && !x?.url)
    },
    getUser: (state): User | null => {
      return state.user
    },
    getBookings: (state): Booking[] => state?.bookings || [],
    isLoggedIn: (state): boolean => {
      if (!state.user) {
        return false
      }

      return Boolean(state.user?.exp && dayjs(state.user.exp * 1000).isAfter(dayjs()))
    },
    username (state): string | null {
      if (!this.isLoggedIn) {
        return null
      } else if (state.user?.name) {
        return state.user.name
      } else if (state.user?.nickname) {
        return state.user.nickname
      }
      return null
    },
    avatar (state): string | null {
      const userImage = getPictureUrl(state.user?.picture?.toString())
      if (!!userImage && userImage.includes('http')) {
        return userImage
      } else if (state.user?.email) {
        return getGravatarByEmail(state.user.email)
      }
      return null
    },
    getMemberActiveSingle (): boolean {
      return this.isLoggedIn && this.memberActiveSingle
    },
    getMemberProfileConfig (): any {
      return this.isLoggedIn && this.memberProfile?.config
    },
    getMemberSingleList (): MemberSingleItem[] | false {
      return this.isLoggedIn && this.memberSingleList
    },
  },
  actions: {
    SET_MEMBER_SINGLE_FINDER_LIST (data: MemberSingleItem[]): void {
      this.memberSingleList = data
    },
    SET_MEMBER_PROFILE (data: MemberProfile): void {
      this.memberProfile = data
    },
    SET_MEMBER_ACTIVE_SINGLE_FINDER (data: boolean): void {
      this.memberActiveSingle = data
    },
    SET_MEMBER_PAGE_TAB (data: string): void {
      this.memberPageTab = data
    },
    SET_MEMBER_PAGE_TABS (data: string[]): void {
      this.memberPageTabs = data
    },
    SET_MEMBER_PAGE_PATH (data: string): void {
      this.memberPagePath = data
    },
    //old mutations
    SET_TOKEN (token: string | null = null): void {
      this.authFeedback = null
      if (import.meta.client) {
        this.token = token
      }
    },
    SET_USER (user: User): void {
      this.user = { ...user }
    },
    SET_LOADING (loading: boolean): void {
      this.loading = loading
    },
    SET_FEEDBACK (feedback: AuthFeedback | null): void {
      this.feedback = feedback
    },
    SET_RESEND_CODE_EMAIL (resendCodeEmail: string | null): void {
      this.resendCodeEmail = resendCodeEmail
    },
    /**
     * Asks whoever handles logins (CognitoContainer) to start one, from anywhere in the tree
     */
    requestLogin (payload: LoginAction | LoginActionWithFeedback | null = null): void {
      this.loginRequest = {
        id: (this.loginRequest?.id || 0) + 1,
        payload,
      }
    },
    SET_BOOKINGS (bookings: Booking[]): void {
      this.bookings = bookings
    },
    POPULATE_BOOKING (booking: Booking): void {
      const index = this.bookings.findIndex(item => item?.data?.ocr?.value === booking?.data?.ocr?.value)
      const list = this.bookings
      list[index] = booking
      this.bookings = [...list]
    },
    SET_FAVOURITES (favourites: Favourite[]): void {
      this.favourites = [...favourites]
    },
    ADD_FAVOURITE (favourite: Favourite): void {
      this.favourites = [...this.favourites, favourite]
    },
    CLEAR_AND_LOGOUT (): void {
      this.$reset()

      if (import.meta.client) {
        this.memberActiveSingle = false
        tryRemoveLocalStorage(localStorageKeys.auth.token)
        tryRemoveLocalStorage(localStorageKeys.auth.user)
      }
    },
    POPULATE_FAVOURITE (favourite: Favourite): void {
      const index = this.favourites.findIndex(({ id }) => String(id) === String(favourite.id))
      if (index !== -1) {
        const list = [...this.favourites]
        list[index] = favourite
        this.favourites = list
      } else {
        // If the favorite doesn't exist, add it
        this.favourites = [...this.favourites, favourite]
      }
    },
    REMOVE_FAVOURITE (tripTypeId: string): void {
      this.favourites = [...this.favourites.filter(({ id }) => String(id) !== String(tripTypeId))]
    },
    SET_AUTH_FEEDBACK (authFeedback: AuthFeedback): void {
      this.authFeedback = authFeedback
    },
    SET_INITIATED (isInitiated: boolean): void {
      this.isInitiated = isInitiated
    },

    //old actions
    async init ({ token }: { token?: string }): Promise<boolean> {
      if (token) {
        this.setToken(token)
      }

      if (!this.isInitiated) {
        await this.listBookings()
      }

      this.SET_INITIATED(true)
      return true
    },

    setUser (user: User): void {
      this.SET_USER(user)

      if (import.meta.client) {
        this.backupState()
      }
    },

    setToken (token: string | null): void {
      this.SET_TOKEN(token)

      if (import.meta.client) {
        this.backupState()
      }
    },

    async listBookings (): Promise<void> {
      const localeStore = useLocaleStore()
      const locale = localeStore.locale
      // @ts-ignore - getter exists but not in inferred type
      const washUrl = (url: string, full = false) => localeStore.washExternalLinkToInternal(url, full)
      let result: any[] = []

      this.SET_BOOKINGS([])
      this.SET_LOADING(true)
      this.SET_FEEDBACK(null)

      try {
        const { data: res } = await apiFetch(`/${locale}/customer/mybookings/list`, {
          method: 'POST',
          body: {
            token: this.getTokenForRequest,
          },
        })
        result = res
        this.SET_BOOKINGS(result.map(booking => ({
          ...booking,
          url: booking?.url ? washUrl(booking.url) : undefined,
        })))
      } catch (e: any) {
        this.SET_LOADING(false)
        const errorMessage = e instanceof Error ? e.message : 'Unknown error'
        this.SET_FEEDBACK(createAuthFeedback(errorMessage, 'unknown', Contexts.ERROR))

        Sentry.captureException(e)
      }

      this.SET_LOADING(false)
    },

    async listFavourites (): Promise<void> {
      const localeStore = useLocaleStore()
      const locale = localeStore.locale
      let result: any[] = []

      this.SET_LOADING(true)
      this.SET_FEEDBACK(null)

      try {
        const { data: res } = await apiFetch(`/${locale}/customer/hearts-triptype/list`, {
          method: 'POST',
          addToken: true,
        })
        result = res
      } catch (e: any) {
        this.SET_LOADING(false)
        const errorMessage = e instanceof Error ? e.message : 'Unknown error'
        this.SET_FEEDBACK(createAuthFeedback(errorMessage, 'unknown', Contexts.ERROR))

        Sentry.captureException(e)
        return
      }

      const parsedResult = result.map((resultItem: any) => {
        const existingFav = this.favourites.find(fav => String(fav.id) === String(resultItem?.triptypeid))
        return existingFav && existingFav.url
          ? existingFav
          : { ...resultItem, id: resultItem?.triptypeid }
      })

      this.SET_LOADING(false)
      this.SET_FAVOURITES(parsedResult)
    },

    async populateFavourites (): Promise<void> {
      const localeStore = useLocaleStore()
      const locale = localeStore.locale
      this.SET_LOADING(true)
      this.SET_FEEDBACK(null)

      const unpopulatedFavourites = this.unpopulatedFavourites

      try {
        const results = await Promise.allSettled(
          unpopulatedFavourites.map(({ id }) => apiFetch(`/${locale}/triptype?id=${id}`))
        )

        results.forEach((result, index) => {
          if (result.status === 'fulfilled') {
            this.POPULATE_FAVOURITE(result.value.data)
            return
          }

          // A 404 is a trip type that no longer exists: the heart stays unpopulated and hidden
          if (getErrorStatus(result.reason) === 404) {
            return
          }

          Sentry.captureMessage('rolfs/admin/could not load favourite', {
            level: 'warning',
            contexts: {
              reason: result.reason,
              id: unpopulatedFavourites[index].id,
            } as any,
            tags: { type: 'UX', source: 'rolfs/admin' },
          })
        })

        this.SET_LOADING(false)
      } catch (e: any) {
        this.SET_LOADING(false)
        const errorMessage = e instanceof Error ? e.message : 'Unknown error'
        this.SET_FEEDBACK(createAuthFeedback(errorMessage, 'unknown', Contexts.ERROR))

        Sentry.captureException(e)
      }
    },

    async addFavourite (tripTypeId: string): Promise<boolean> {
      const localeStore = useLocaleStore()
      const locale = localeStore.locale
      let result: any
      try {
        const { data: res } = await apiFetch(`/${locale}/customer/hearts-triptype/add`, {
          method: 'POST',
          addToken: true,
          body: {
            triptypeid: tripTypeId,
          },
        })
        result = res
      } catch (e: any) {
        Sentry.captureException(e)
        return false
      }

      if (result.status === 'success') {
        this.ADD_FAVOURITE({ id: tripTypeId })

        // Fetch the full trip data for the newly added favorite to load images for new favorite cards
        try {
          const { data: tripData } = await apiFetch(`/${locale}/triptype?id=${tripTypeId}`)
          if (tripData) {
            this.POPULATE_FAVOURITE(tripData)
          }
        } catch (error: any) {
          //
        }

        return true
      }

      return false
    },

    async removeFavourite (tripTypeId: string): Promise<boolean> {
      const localeStore = useLocaleStore()
      const locale = localeStore.locale
      try {
        const { data: result } = await apiFetch(`/${locale}/customer/hearts-triptype/delete`, {
          method: 'POST',
          addToken: true,
          body: {
            triptypeid: tripTypeId,
          },
        })
        if (result.status === 'success') {
          this.REMOVE_FAVOURITE(tripTypeId)
          return true
        } else {
          Sentry.captureMessage('rolfs/admin/could not add favourite', {
            level: 'warning',
            contexts: { tripTypeId, ...result },
            tags: { type: 'UX', source: 'rolfs/admin' },
          })
        }
      } catch (e: any) {
        Sentry.captureException(e)
      }
      return false
    },

    toggleFavourite (tripTypeId: string): Promise<boolean> {
      const tripTypeIdStr = String(tripTypeId)
      const isFavorited = this.favourites.some(({ id }) => String(id) === tripTypeIdStr)
      const method = isFavorited ? 'remove' : 'add'

      // update UI immediately
      if (isFavorited) {
        this.REMOVE_FAVOURITE(tripTypeId)
      } else {
        this.ADD_FAVOURITE({ id: tripTypeId })
      }

      // Call API in background and revert if it fails
      return this[`${method}Favourite`](tripTypeId).then(success => {
        if (!success && !isFavorited) {
          // Revert optimistic add if API failed
          this.REMOVE_FAVOURITE(tripTypeId)
        } else if (!success && isFavorited) {
          // Revert optimistic remove if API failed
          this.ADD_FAVOURITE({ id: tripTypeId })
        }
        return success
      })
    },

    clearUserForLogout (): void {
      this.CLEAR_AND_LOGOUT()
      this.backupState()
    },

    backupState (): void {
      try {
        trySetLocalStorage(localStorageKeys.auth.user, this.user ? JSON.stringify(this.user) : '')
        trySetLocalStorage(localStorageKeys.auth.token, this.token || '')
      } catch {
        // ignore error
      }
    },

    async fetchMemberProfile (): Promise<MemberProfile | undefined> {
      const localeStore = useLocaleStore()
      const locale = localeStore.locale
      let result: MemberProfile
      try {
        const { data: res } = await apiFetch(`/${locale}/customer/profile`, {
          method: 'POST',
          addToken: true,
        })
        result = res
        this.SET_MEMBER_PROFILE(result)

        // Check the single_finder key and set memberActiveSingle accordingly
        if (result.config && result.config.single_finder !== undefined) {
          this.SET_MEMBER_ACTIVE_SINGLE_FINDER(result.config.single_finder === 1)
        } else {
          this.SET_MEMBER_ACTIVE_SINGLE_FINDER(false)
        }
      } catch (e: any) {
        Sentry.captureException(e)
        return undefined
      }

      return result
    },

    async updateMemberProfile (body: Record<string, any>): Promise<MemberProfile | undefined> {
      const localeStore = useLocaleStore()
      const locale = localeStore.locale
      let result: MemberProfile
      try {
        const { data: res } = await apiFetch(`/${locale}/customer/profile/update`, {
          method: 'POST',
          addToken: true,
          body: {
            ...body,
          },
        })
        result = res
        this.SET_MEMBER_PROFILE(result)
      } catch (e: any) {
        Sentry.captureException(e)
        return undefined
      }

      return result
    },

    async fetchMemberSingleFinderList (): Promise<MemberSingleItem[] | undefined> {
      const localeStore = useLocaleStore()
      const locale = localeStore.locale
      let result: MemberSingleItem[]
      try {
        const { data: res } = await apiFetch(`/${locale}/customer/single-finder/list`, {
          method: 'POST',
          addToken: true,
        })
        result = res
        this.SET_MEMBER_SINGLE_FINDER_LIST(result)
      } catch (e: any) {
        Sentry.captureException(e)
        return undefined
      }

      return result
    },

    async memberSingleFinderAdd (tripid: string): Promise<any> {
      const localeStore = useLocaleStore()
      const locale = localeStore.locale
      let result: any
      try {
        const { data: res } = await apiFetch(`/${locale}/customer/single-finder/add`, {
          method: 'POST',
          addToken: true,
          body: {
            trip_id: tripid,
          },
        })
        result = res
      } catch (e: any) {
        Sentry.captureException(e)
        return undefined
      }

      return result
    },

    async memberSingleFinderRenew (tripid: string): Promise<any> {
      const localeStore = useLocaleStore()
      const locale = localeStore.locale
      let result: any
      try {
        const { data: res } = await apiFetch(`/${locale}/customer/single-finder/renew`, {
          method: 'POST',
          addToken: true,
          body: {
            trip_id: tripid,
          },
        })
        result = res
      } catch (e: any) {
        Sentry.captureException(e)
        return undefined
      }

      return result
    },

    async memberSingleFinderDelete (tripid: string): Promise<any> {
      const localeStore = useLocaleStore()
      const locale = localeStore.locale
      let result: any
      try {
        const { data: res } = await apiFetch(`/${locale}/customer/single-finder/remove`, {
          method: 'POST',
          addToken: true,
          body: {
            trip_id: tripid,
          },
        })
        result = res
      } catch (e: any) {
        Sentry.captureException(e)
        return undefined
      }

      return result
    },

    async memberSingleFinderContact (payload: Record<string, any>): Promise<any> {
      const localeStore = useLocaleStore()
      const locale = localeStore.locale
      let result: any
      try {
        const { data: res } = await apiFetch(`/${locale}/customer/single-finder/contact`, {
          method: 'POST',
          addToken: true,
          throwOnFail: false,
          body: {
            ...payload,
          },
        })
        result = res
      } catch (e: any) {
        Sentry.captureException(e)
        return undefined
      }

      return result
    },
  },
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useUserStore, import.meta.hot))
}

export { useUserStore }
